import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LEGAL_UPDATED_AT } from '@/constants/site'

interface LegalPageProps {
    title: string
    description: string
    intro: ReactNode
    children: ReactNode
}

interface LegalSectionProps {
    title: string
    children: ReactNode
}

export const LegalSection: FC<LegalSectionProps> = ({ title, children }): ReactNode => {
    return (
        <section className='border-t border-border pt-8'>
            <h2 className='font-heading text-xl font-bold tracking-tight sm:text-2xl'>
                {title}
            </h2>

            <div className='mt-4 space-y-4 leading-relaxed text-foreground/80 [&_a]:font-semibold [&_a]:text-brand-700 [&_a]:underline [&_a]:underline-offset-2 [&_li]:pl-1 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5'>
                {children}
            </div>
        </section>
    )
}

export const LegalPage: FC<LegalPageProps> = ({
    title,
    description,
    intro,
    children
}): ReactNode => {
    return (
        <Layout>
            <Seo title={title} description={description} />

            <article className='mx-auto max-w-3xl px-5 pt-32 pb-24 sm:px-8 sm:pt-40 sm:pb-32'>
                <Link
                    href='/'
                    className='group inline-flex items-center gap-2 rounded-md text-sm font-bold text-brand-700 hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-400'
                >
                    <ArrowLeft className='size-4 transition-transform duration-200 group-hover:-translate-x-1' aria-hidden='true' />
                    Volver al inicio
                </Link>

                <h1 className='mt-8 font-heading text-4xl leading-tight font-extrabold tracking-[-0.035em] sm:text-5xl'>
                    {title}
                </h1>

                <p className='mt-4 text-sm text-muted-foreground'>
                    Última actualización: {LEGAL_UPDATED_AT}
                </p>

                <div className='mt-8 text-lg leading-relaxed text-foreground/80'>
                    {intro}
                </div>

                <div className='mt-12 space-y-10'>
                    {children}
                </div>
            </article>
        </Layout>
    )
}
